import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Info, Dumbbell, PersonStanding, Baby, Calendar, Star, ArrowUpRight, Users } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { PRICING_PLANS } from "@/data/content";
import { useAdminContent } from "@/lib/adminContent";

const TAB_ICONS = {
  individual: Calendar,
  sala: Dumbbell,
  clase: PersonStanding,
  perioada: Users,
  copii: Baby,
};
const TAB_IDS = ["individual", "perioada", "sala", "clase", "copii"];

const cardsContainerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.02,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.035,
      staggerDirection: -1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: -28, scale: 0.985 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.46,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  exit: {
    opacity: 0,
    y: 14,
    scale: 0.99,
    transition: {
      duration: 0.18,
      ease: [0.4, 0, 1, 1],
    },
  },
};

const Pricing = ({ onOpenOrder, onOpenAerobicInfo, onOpenTrainers }) => {
  const { t, lang } = useI18n();
  const { plansById } = useAdminContent(lang);
  const [active, setActive] = useState(TAB_IDS[0]);
  const tabsScrollRef = useRef(null);
  const tabRefs = useRef({});
  const [activeTabMarker, setActiveTabMarker] = useState({ left: 0, width: 0, ready: false });
  const cards = (PRICING_PLANS[active] || [])
    .map((card, idx) => {
      const ap = plansById?.[card.id];
      if (!ap) return { ...card, order: idx };
      return {
        ...card,
        price: Number(ap.price ?? card.price),
        name: ap.name,
        features: ap.features,
        order: Number.isFinite(Number(ap.order)) ? Number(ap.order) : idx,
      };
    })
    .sort((a, b) => {
      const ao = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
      const bo = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
      return ao - bo;
    });

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateMarker = () => {
      const target = tabRefs.current[active];
      if (!target) return;

      setActiveTabMarker({
        left: target.offsetLeft,
        width: target.offsetWidth,
        ready: true,
      });
    };

    // Run once now and once on the next frame so marker width
    // follows translated labels immediately after language switch.
    updateMarker();
    const rafId = window.requestAnimationFrame(updateMarker);
    window.addEventListener("resize", updateMarker);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateMarker);
    };
  }, [active, lang]);

  useEffect(() => {
    // Mobile UX: keep selected tab aligned to the left edge when possible.
    if (typeof window === "undefined" || window.innerWidth >= 640) return;
    const container = tabsScrollRef.current;
    const target = tabRefs.current[active];
    if (!container || !target) return;

    const targetLeft = target.offsetLeft - container.offsetLeft - 6;
    const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const nextLeft = Math.min(Math.max(0, targetLeft), maxLeft);
    container.scrollTo({ left: nextLeft, behavior: "smooth" });
  }, [active]);

  return (
    <section id="pricing" className="section relative" data-testid="pricing-section">
      <div className="absolute top-32 left-1/2 -translate-x-1/2 hidden sm:block w-[40rem] h-[40rem] rounded-full bg-[var(--accent)]/8 blur-[120px] pointer-events-none" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-center max-w-4xl mx-auto leading-[1.1]"
          data-testid="pricing-title"
        >
          {t("pricing.title1")}
          <br />
          <span className="accent-text italic font-light">{t("pricing.title2")}</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-3 sm:mt-4 text-sm sm:text-base text-[var(--text-dim)] text-center max-w-2xl mx-auto px-2"
        >
          {t("pricing.subtitle")}
        </motion.p>

        {/* Tabs - horizontal scroll on mobile */}
        <div className="mt-8 sm:mt-12 flex justify-start sm:justify-center -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="pricing-tabs">
          <div
            ref={tabsScrollRef}
            className="glass relative rounded-2xl p-1.5 flex gap-1 overflow-x-auto max-w-full"
            style={{ scrollbarWidth: "none" }}
          >
            {activeTabMarker.ready && (
              <motion.div
                aria-hidden
                className="absolute top-1.5 bottom-1.5 rounded-xl"
                style={{
                  background: "linear-gradient(135deg, var(--accent), var(--accent-2))",
                  boxShadow: "0 8px 24px -10px var(--accent-glow)",
                }}
                animate={{ x: activeTabMarker.left, width: activeTabMarker.width }}
                transition={{ type: "spring", stiffness: 380, damping: 34, mass: 0.7 }}
              />
            )}
            {TAB_IDS.map((id) => {
              const Icon = TAB_ICONS[id] || Calendar;
              const isActive = active === id;
              return (
                <button
                  key={id}
                  ref={(el) => {
                    if (el) tabRefs.current[id] = el;
                  }}
                  onClick={() => setActive(id)}
                  data-testid={`pricing-tab-${id}`}
                  className={`relative px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                    isActive ? "text-[var(--on-accent)]" : "text-[var(--text-dim)] hover:text-[var(--text)]"
                  }`}
                >
                  <span className="relative z-[1] flex items-center gap-2">
                    <Icon size={14} />
                    {t(`pricing.tabs.${id}.label`)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active}
            variants={cardsContainerVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
          >
            {cards.map((card, i) => {
              const planName = card.name || t(`pricing.plans.${card.id}.name`);
              const staticFeatures = t(`pricing.plans.${card.id}.features`);
              const featureList =
                Array.isArray(card.features) && card.features.length ? card.features : Array.isArray(staticFeatures) ? staticFeatures : [];
              const featureRows = featureList.map((item) =>
                typeof item === "string"
                  ? { label: item, negative: false }
                  : item && typeof item === "object" && "text" in item
                    ? { label: item.text, negative: !!item.negative }
                    : { label: String(item), negative: false }
              );
              const priceUnit = t(`pricing.plans.${card.id}.pricePer`, "") || t("pricing.pricePer");
              const badge = card.popular
                ? (active === "perioada" ? t("pricing.badgeBest") : t("pricing.badgePopular"))
                : null;
              const showAerobicInfo = active === "clase" && (card.id === "clase-aerobic" || card.id === "clase-aerobic-sala");

              return (
                <motion.article
                  key={card.id}
                  variants={cardVariants}
                  className={`relative glass rounded-2xl sm:rounded-3xl p-6 sm:p-7 card-hover group flex flex-col ${
                    badge ? "lg:-mt-2 lg:mb-2" : ""
                  }`}
                  data-testid={`pricing-card-${active}-${i}`}
                >
                  {badge && (
                    <div className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-[var(--accent)] to-[var(--accent-2)] text-[var(--on-accent)] flex items-center gap-1.5 shadow-lg shadow-orange-500/20">
                      <Star size={11} className="fill-[var(--on-accent)]" />
                      {badge}
                    </div>
                  )}
                  {showAerobicInfo && (
                    <button
                      type="button"
                      onClick={() => onOpenAerobicInfo?.()}
                      className="absolute top-4 right-4 sm:top-5 sm:right-5 w-10 h-10 rounded-xl glass flex items-center justify-center hover:border-[var(--accent)]/45 hover:bg-[var(--accent)]/10 transition-colors"
                      aria-label={t("pricing.aerobicInfo.open")}
                      data-testid={`pricing-aerobic-info-${card.id}`}
                    >
                      <Info size={15} className="text-[var(--text-dim)]" />
                    </button>
                  )}

                  <h3 className="font-display text-lg sm:text-xl font-semibold leading-tight pr-4" data-testid={`pricing-card-name-${i}`}>
                    {planName}
                  </h3>

                  <div className="mt-5 sm:mt-6 flex items-baseline gap-1.5">
                    <span className="font-display text-4xl sm:text-6xl font-bold accent-text tabular-nums tracking-tight" data-testid={`pricing-card-price-${i}`}>
                      {card.price}
                    </span>
                    <span className="text-sm text-[var(--text-dim)] font-medium">{priceUnit}</span>
                  </div>

                  <div className="my-5 sm:my-6 h-px bg-gradient-to-r from-transparent via-[var(--border-strong)] to-transparent" />

                  <ul className="space-y-2.5 sm:space-y-3">
                    {featureRows.map(({ label, negative }, j) => (
                      <li key={j} className="flex items-start gap-3 text-xs sm:text-sm text-[var(--text)] leading-relaxed">
                        <span
                          className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border ${
                            negative
                              ? "bg-red-500/10 border-red-500/35"
                              : "bg-[var(--accent)]/15 border-[var(--accent)]/25"
                          }`}
                        >
                          {negative ? (
                            <X size={11} className="text-red-500" strokeWidth={3} aria-hidden />
                          ) : (
                            <Check size={11} className="text-[var(--accent)]" strokeWidth={3} aria-hidden />
                          )}
                        </span>
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-6 sm:pt-7 flex flex-col gap-3">
                    {active === "perioada" && (
                      <button
                        type="button"
                        onClick={() => onOpenTrainers?.()}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full py-3 sm:py-3.5 font-medium text-sm transition border border-[rgba(255,181,71,0.35)] shadow-[0_16px_40px_-18px_var(--accent-glow)] hover:brightness-105"
                        style={{
                          background: "linear-gradient(135deg, var(--accent), var(--accent-2))",
                          color: "var(--on-accent)",
                        }}
                        data-testid={`pricing-card-trainers-${active}-${i}`}
                      >
                        {t("pricing.viewTrainersCta")}
                        <ArrowUpRight size={15} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onOpenOrder?.(card.id)}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full py-3 sm:py-3.5 font-medium text-sm transition ${
                        badge
                          ? "btn-primary"
                          : "border border-[var(--border-strong)] hover:border-[var(--accent)]/55 hover:bg-[var(--accent)]/10 text-[var(--text)]"
                      }`}
                      data-testid={`pricing-card-cta-${active}-${i}`}
                    >
                      {t("pricing.chooseCta")}
                      <ArrowUpRight size={15} />
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Pricing;
